// SVG Export functionality
export const exportSelectionToSVG = (
  svgElement: SVGSVGElement,
  fileName?: string
): void => {
  if (!svgElement) {
    alert('Canvas not available for export');
    return;
  }

  try {
    // Get dimensions from the SVG element
    const rect = svgElement.getBoundingClientRect();
    const exportWidth = rect.width;
    const exportHeight = rect.height;

    // Clone the SVG element
    const svgClone = svgElement.cloneNode(true) as SVGSVGElement;

    // Debug logging
    console.log('Original SVG children count:', svgElement.children.length);
    console.log('Cloned SVG children count:', svgClone.children.length);
    console.log(
      'Original SVG HTML (first 1000 chars):',
      svgElement.outerHTML.substring(0, 1000)
    );

    // Set proper dimensions and attributes for export
    svgClone.setAttribute('width', exportWidth.toString());
    svgClone.setAttribute('height', exportHeight.toString());
    svgClone.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

    // Find and process all foreign objects
    const foreignObjects = svgClone.querySelectorAll('foreignObject');
    console.log(`Found ${foreignObjects.length} foreign objects`);

    console.log('=== DETAILED SVG STRUCTURE ANALYSIS ===');
    // Check for native SVG text elements
    const nativeTextElements = svgClone.querySelectorAll('text');
    console.log('Native SVG text elements found:', nativeTextElements.length);

    // Analyze each foreign object in detail
    foreignObjects.forEach((fo, index) => {
      const hasImg = !!fo.querySelector('img');
      const hasTextarea = !!fo.querySelector('textarea');
      const x = fo.getAttribute('x');
      const y = fo.getAttribute('y');
      const width = fo.getAttribute('width');
      const height = fo.getAttribute('height');

      console.log(`Foreign object ${index}:`, {
        hasImg,
        hasTextarea,
        x,
        y,
        width,
        height,
      });

      // Analyze content in detail
      const hasSvg = !!fo.querySelector('svg');
      const hasIcon = !!fo.querySelector('[data-lucide]');
      const hasTextContent = !!(
        fo.textContent && fo.textContent.trim().length > 0
      );
      const hasTextDiv = !!fo.querySelector('div');
      const textContent = fo.textContent?.trim() || '';

      console.log('Foreign object analysis:', {
        hasSvg,
        hasIcon,
        hasTextContent,
        hasTextDiv,
        textContent: textContent.substring(0, 100),
      });

      if (hasImg) {
        // Convert image to SVG image element
        const img = fo.querySelector('img') as HTMLImageElement;
        if (img && img.src) {
          console.log(
            'Converting image to SVG:',
            img.src.substring(0, 50) + '...'
          );

          try {
            const svgImage = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'image'
            );
            svgImage.setAttribute('x', fo.getAttribute('x') || '0');
            svgImage.setAttribute('y', fo.getAttribute('y') || '0');
            svgImage.setAttribute('width', fo.getAttribute('width') || '100');
            svgImage.setAttribute('height', fo.getAttribute('height') || '100');
            svgImage.setAttributeNS(
              'http://www.w3.org/1999/xlink',
              'href',
              img.src
            );

            // Apply any transform from the foreign object
            const transform = fo.getAttribute('transform');
            if (transform) {
              svgImage.setAttribute('transform', transform);
            }

            if (fo.parentNode) {
              fo.parentNode.replaceChild(svgImage, fo);
            }
            console.log('Successfully converted image to SVG element');
          } catch (error) {
            console.warn('Failed to convert image:', error);
            if (fo.parentNode) {
              fo.parentNode.removeChild(fo);
            }
          }
        }
      } else if (hasSvg) {
        // Extract SVG content from foreign object
        const svgContent = fo.querySelector('svg');
        if (svgContent) {
          try {
            const svgClone = svgContent.cloneNode(true) as SVGSVGElement;

            // Position the SVG element
            svgClone.setAttribute('x', fo.getAttribute('x') || '0');
            svgClone.setAttribute('y', fo.getAttribute('y') || '0');
            svgClone.setAttribute('width', fo.getAttribute('width') || '100');
            svgClone.setAttribute('height', fo.getAttribute('height') || '100');

            // Apply any transform from the foreign object
            const transform = fo.getAttribute('transform');
            if (transform) {
              svgClone.setAttribute('transform', transform);
            }

            if (fo.parentNode) {
              fo.parentNode.replaceChild(svgClone, fo);
            }
            console.log(
              'Successfully converted SVG foreignObject to native SVG'
            );
          } catch (error) {
            console.warn('Failed to convert SVG foreignObject:', error);
            if (fo.parentNode) {
              fo.parentNode.removeChild(fo);
            }
          }
        }
      } else if (hasIcon) {
        // Create a visual placeholder for icons
        const iconGroup = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'g'
        );

        const iconRect = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'rect'
        );
        iconRect.setAttribute('x', fo.getAttribute('x') || '0');
        iconRect.setAttribute('y', fo.getAttribute('y') || '0');
        iconRect.setAttribute('width', fo.getAttribute('width') || '24');
        iconRect.setAttribute('height', fo.getAttribute('height') || '24');
        iconRect.setAttribute('fill', '#4a5568');
        iconRect.setAttribute('stroke', '#718096');
        iconRect.setAttribute('stroke-width', '1');
        iconRect.setAttribute('rx', '3');

        const iconText = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'text'
        );
        iconText.setAttribute(
          'x',
          String(
            Number(fo.getAttribute('x') || '0') +
              Number(fo.getAttribute('width') || '24') / 2
          )
        );
        iconText.setAttribute(
          'y',
          String(
            Number(fo.getAttribute('y') || '0') +
              Number(fo.getAttribute('height') || '24') / 2 +
              4
          )
        );
        iconText.setAttribute('text-anchor', 'middle');
        iconText.setAttribute('font-family', 'Arial, sans-serif');
        iconText.setAttribute('font-size', '10');
        iconText.setAttribute('fill', '#e2e8f0');
        iconText.textContent = '⚡';

        iconGroup.appendChild(iconRect);
        iconGroup.appendChild(iconText);

        // Apply any transform from the foreign object
        const transform = fo.getAttribute('transform');
        if (transform) {
          iconGroup.setAttribute('transform', transform);
        }

        if (fo.parentNode) {
          fo.parentNode.replaceChild(iconGroup, fo);
        }
        console.log('Successfully converted icon to SVG placeholder');
      } else if (textContent && textContent.length > 0) {
        // Convert text content to SVG text element
        try {
          console.log(
            'Converting text foreignObject to SVG text:',
            textContent
          );

          // Find the div element inside the foreign object
          const textDiv = fo.querySelector('div');
          if (!textDiv) {
            console.warn('No div found in foreign object');
            if (fo.parentNode) {
              fo.parentNode.removeChild(fo);
            }
            return;
          }

          // Parse the inline styles from the div's style attribute
          const inlineStyle = textDiv.getAttribute('style') || '';
          console.log('Div inline style:', inlineStyle);

          // Extract style values using regex
          const getStyleValue = (property: string): string => {
            const regex = new RegExp(`${property}:\\s*([^;]+)`);
            const match = inlineStyle.match(regex);
            return match ? match[1]!.trim() : '';
          };

          const fontSize = getStyleValue('font-size');
          const fontFamily = getStyleValue('font-family').replace(
            /&quot;/g,
            '"'
          );
          const color = getStyleValue('color');
          const textAlign = getStyleValue('text-align');
          const fontWeight = getStyleValue('font-weight');
          const letterSpacing = getStyleValue('letter-spacing');
          const alignItems = getStyleValue('align-items');
          const justifyContent = getStyleValue('justify-content');
          const whiteSpace = getStyleValue('white-space');

          console.log('Extracted styles:', {
            fontSize,
            fontFamily,
            color,
            textAlign,
            fontWeight,
            letterSpacing,
            alignItems,
            justifyContent,
            whiteSpace,
          });

          const svgText = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'text'
          );

          // Position - account for how text is positioned in foreign objects
          const foX = Number(fo.getAttribute('x') || '0');
          const foY = Number(fo.getAttribute('y') || '0');
          const foWidth = Number(fo.getAttribute('width') || '0');
          const foHeight = Number(fo.getAttribute('height') || '0');
          const fontSizeValue = parseFloat(fontSize) || 16;

          // For shape text, the foreign object has padding and the text is centered
          let textX = foX;
          let textY = foY;

          // Adjust for text alignment within the foreign object
          const horizontalAlign = textAlign || 'center';
          const verticalAlign = alignItems || 'center';

          // Horizontal positioning
          if (horizontalAlign === 'center') {
            textX = foX + foWidth / 2;
          } else if (horizontalAlign === 'right' || horizontalAlign === 'end') {
            textX = foX + foWidth;
          } else {
            textX = foX; // left alignment
          }

          // Vertical positioning - account for flex centering
          if (verticalAlign === 'center') {
            textY = foY + foHeight / 2 + fontSizeValue * 0.3;
          } else if (verticalAlign === 'flex-end') {
            textY = foY + foHeight - fontSizeValue * 0.2;
          } else {
            textY = foY + fontSizeValue;
          }

          svgText.setAttribute('x', String(textX));
          svgText.setAttribute('y', String(textY));

          // Check if this is a textarea (editing mode) - if so, skip it
          const textarea = fo.querySelector('textarea');
          if (textarea) {
            console.log('Skipping textarea in SVG export');
            if (fo.parentNode) {
              fo.parentNode.removeChild(fo);
            }
            return;
          }

          // Font properties - use the exact extracted font family
          console.log('Using extracted font family:', fontFamily);

          // Use the exact font family from the extracted styles
          // For better compatibility, ensure web-safe fonts are prioritized
          const finalFontFamily =
            fontFamily ||
            'Caveat, Kalam, "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive';

          // For SVG export, use a font stack that works better in standalone SVG files
          const svgFontFamily = finalFontFamily.includes('Caveat')
            ? '"Caveat", "Kalam", "Comic Sans MS", "Marker Felt", "Bradley Hand", cursive'
            : finalFontFamily;

          console.log('Setting font-family attribute to:', svgFontFamily);
          svgText.setAttribute('font-family', svgFontFamily);
          svgText.setAttribute('font-size', fontSize || '16px');
          svgText.setAttribute('font-weight', fontWeight || 'normal');
          svgText.setAttribute('font-style', 'normal');

          // Use letter spacing from extracted styles or default based on font family
          const extractedLetterSpacing =
            letterSpacing ||
            ((fontFamily || '').includes('JetBrains Mono') ||
            (fontFamily || '').includes('Fira Code') ||
            (fontFamily || '').includes('Consolas') ||
            (fontFamily || '').includes('Monaco') ||
            (fontFamily || '').includes('Courier')
              ? '0'
              : '0.5px');
          svgText.setAttribute('letter-spacing', extractedLetterSpacing);

          // Color
          if (color && color !== 'rgba(0, 0, 0, 0)') {
            svgText.setAttribute('fill', color);
          } else {
            svgText.setAttribute('fill', '#ffffff');
          }

          // Set text anchor based on horizontal alignment
          if (horizontalAlign === 'center') {
            svgText.setAttribute('text-anchor', 'middle');
          } else if (horizontalAlign === 'right' || horizontalAlign === 'end') {
            svgText.setAttribute('text-anchor', 'end');
          } else {
            svgText.setAttribute('text-anchor', 'start');
          }

          // Handle multi-line text and preserve whitespace
          if (
            whiteSpace === 'pre' ||
            whiteSpace === 'pre-wrap' ||
            whiteSpace === 'pre-line'
          ) {
            const lines = textContent.split('\n');

            // For multi-line text, adjust the starting position so the text block is centered
            if (lines.length > 1 && verticalAlign === 'center') {
              const totalTextHeight = lines.length * fontSizeValue * 1.2;
              const adjustedY =
                foY + foHeight / 2 - totalTextHeight / 2 + fontSizeValue * 0.8;
              svgText.setAttribute('y', String(adjustedY));
            }

            lines.forEach((line, index) => {
              const tspan = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'tspan'
              );
              tspan.setAttribute('x', svgText.getAttribute('x') || '0');
              tspan.setAttribute('dy', index === 0 ? '0' : '1.2em');
              tspan.textContent = line;
              svgText.appendChild(tspan);
            });
          } else {
            svgText.textContent = textContent;
          }

          // Apply any transform from the foreign object
          const transform = fo.getAttribute('transform');
          if (transform) {
            svgText.setAttribute('transform', transform);
          }

          if (fo.parentNode) {
            fo.parentNode.replaceChild(svgText, fo);
          }
          console.log(
            'Successfully converted text foreignObject to SVG text with preserved styles'
          );
        } catch (error) {
          console.warn('Failed to convert text foreignObject:', error);
          if (fo.parentNode) {
            fo.parentNode.removeChild(fo);
          }
        }
      } else {
        // Remove other foreign objects (truly unknown content)
        console.log('Removing other foreign object (unknown content)');
        if (fo.parentNode) {
          fo.parentNode.removeChild(fo);
        }
      }
    });

    // Add font link to SVG for web font support
    let defs = svgClone.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svgClone.insertBefore(defs, svgClone.firstChild);
    }

    // Add style element with Google Fonts import
    const style = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'style'
    );
    style.setAttribute('type', 'text/css');
    style.textContent =
      '@import url("https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&family=Caveat:wght@400;500;600;700&display=swap");';
    defs.appendChild(style);

    // Set background
    const background = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'rect'
    );
    background.setAttribute('x', '0');
    background.setAttribute('y', '0');
    background.setAttribute('width', exportWidth.toString());
    background.setAttribute('height', exportHeight.toString());
    background.setAttribute('fill', '#1a1a1a');
    svgClone.insertBefore(background, svgClone.firstChild);

    // Convert SVG to string
    const svgData = new XMLSerializer().serializeToString(svgClone);

    // Debug: Log final SVG content
    console.log('Final SVG children count:', svgClone.children.length);
    console.log(
      'Final SVG data (first 2000 chars):',
      svgData.substring(0, 2000)
    );
    console.log('Contains text elements:', svgData.includes('<text'));
    console.log('Contains image elements:', svgData.includes('<image'));

    // Also check for regular text elements in the original SVG
    const originalTextElements = svgElement.querySelectorAll('text');
    const clonedTextElements = svgClone.querySelectorAll('text');
    console.log('Original SVG text elements:', originalTextElements.length);
    console.log('Cloned SVG text elements:', clonedTextElements.length);

    originalTextElements.forEach((text, index) => {
      console.log(`Original text ${index}:`, {
        content: text.textContent,
        x: text.getAttribute('x'),
        y: text.getAttribute('y'),
        fill: text.getAttribute('fill'),
        parentTagName: text.parentElement?.tagName,
      });
    });

    // Add XML declaration for proper SVG file
    const svgWithDeclaration = `<?xml version="1.0" encoding="UTF-8"?>\n${svgData}`;

    const svgBlob = new Blob([svgWithDeclaration], {
      type: 'image/svg+xml;charset=utf-8',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(svgBlob);
    link.download = fileName || `canvas-export-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('SVG export failed:', error);
    alert('Failed to export SVG: ' + (error as Error).message);
  }
};
